import React, { useState, useEffect } from 'react';
import {
	TextField,
	Button,
	IconButton,
	Typography,
	ToggleButtonGroup,
	ToggleButton,
	CircularProgress,
	Grid2,
	Box,
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { toast } from 'react-toastify';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import type { FormData } from '../../../types';
import { deleteProductImage } from '../../../services/api/ProductAPI';

const sizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

interface ColorVariantImage {
	url?: string;
	file?: File;
	isNew?: boolean;
	preview?: string;
	id?: string;
	imageAltTag?: string;
}

interface SizesVariant {
	_id?: string;
	size: string;
}

interface EnhancedColorVariant {
	colorName: string;
	colorCode: string;
	price: string | number;
	strickoutPrice: string | number;
	sizes: SizesVariant[];
	colorImage: ColorVariantImage[];
	imageAltTags: string;
	_id?: string;
}

const ColorVariants: React.FC<{
	formData: FormData;
	setFormData: React.Dispatch<React.SetStateAction<FormData>>;
	productId?: string;
	disabled?: boolean;
}> = ({ formData, setFormData, productId, disabled }) => {
	const [deletingImages, setDeletingImages] = useState<{ [key: string]: boolean }>({});
	const [loading, setLoading] = useState(false);
	console.log(formData, 'variant>>');
	const colorVariants = formData.colorVariants || [];

	useEffect(() => {
		if (productId && colorVariants.length > 0) {
			setLoading(true);
			try {
				const transformedVariants = colorVariants.map((variant) => {
					const colorImages = Array.isArray(variant.colorImage)
						? variant.colorImage.map((img: string | any) => {
								if (typeof img === 'string') {
									return {
										url: img,
										isNew: false,
										imageAltTag: '',
									};
								}
								return {
									...img,
									imageAltTag: img.imageAltTag || '',
								};
							})
						: [];

					return {
						colorName: variant.colorName || '',
						colorCode: variant.colorCode || '#c62020',
						price: variant.price || '',
						strickoutPrice: variant.strickoutPrice || '',
						sizes:
							variant.sizes?.map((size: string | { size: string }) =>
								typeof size === 'string' ? { size } : size
							) || [],
						colorImage: colorImages,
						_id: variant._id,
						imageAltTag: variant.imageAltTag,
					};
				});
				console.log(transformedVariants, 'transformedVariants');

				setFormData((prev) => ({
					...prev,
					colorVariants: transformedVariants,
				}));
			} catch (error) {
				console.error('Error transforming color variants:', error);
				toast.error('Failed to load color variants');
			} finally {
				setLoading(false);
			}
		}
	}, [productId, setFormData]);

	const handleColorVariantChange = (
		index: number,
		field: keyof EnhancedColorVariant,
		value: any
	) => {
		const newColorVariants = [...formData.colorVariants];
		newColorVariants[index] = {
			...newColorVariants[index],
			[field]: value,
		};
		setFormData({ ...formData, colorVariants: newColorVariants });
	};

	const handleColorImageChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files ? Array.from(e.target.files) : [];
		if (files.length === 0) return;

		const newColorVariants = [...formData.colorVariants];
		const newImages = files.map((file) => ({
			file,
			isNew: true,
			preview: URL.createObjectURL(file),
			imageAltTag: '',
		}));

		newColorVariants[index].colorImage = [...newColorVariants[index].colorImage, ...newImages];
		setFormData({ ...formData, colorVariants: newColorVariants });
	};

	const handleImageAltTagChange = (variantIndex: number, imageIndex: number, value: string) => {
		const updatedVariants = [...formData.colorVariants];
		const imageAltTagArray = [...(updatedVariants[variantIndex].imageAltTag || [])];
		imageAltTagArray[imageIndex] = value;

		updatedVariants[variantIndex] = {
			...updatedVariants[variantIndex],
			imageAltTag: imageAltTagArray,
		};

		setFormData({ ...formData, colorVariants: updatedVariants });
	};

	const handleAddColorVariant = () => {
		setFormData((prev) => ({
			...prev,
			colorVariants: [
				...prev.colorVariants,
				{
					colorName: '',
					colorCode: '#c62020',
					price: '',
					strickoutPrice: '',
					sizes: [],
					colorImage: [],
					imageAltTag: '',
				},
			],
		}));
	};

	const handleRemoveColorVariant = (index: number) => {
		formData.colorVariants[index].colorImage.forEach((img) => {
			if (img.preview) URL.revokeObjectURL(img.preview);
		});

		setFormData((prev) => ({
			...prev,
			colorVariants: prev.colorVariants.filter((_, i) => i !== index),
		}));
	};

	const handleRemoveImage = async (variantIndex: number, imgIndex: number) => {
		const variant = colorVariants[variantIndex];
		const imageToDelete = variant.colorImage[imgIndex];
		const imageKey = `${variantIndex}-${imgIndex}`;

		if (imageToDelete.isNew) {
			if (imageToDelete.preview) {
				URL.revokeObjectURL(imageToDelete.preview);
			}

			const newColorVariants = [...colorVariants];
			newColorVariants[variantIndex].colorImage = newColorVariants[
				variantIndex
			].colorImage.filter((_, i) => i !== imgIndex);

			setFormData({ ...formData, colorVariants: newColorVariants });
			return;
		}

		if (!formData.productId || !formData?._id) {
			toast.error('Cannot delete image - missing required data');
			return;
		}

		try {
			setDeletingImages((prev) => ({ ...prev, [imageKey]: true }));

			const response = await deleteProductImage(formData?.productId, {
				imageUrl: imageToDelete.url,
			});
			if (response.success) {
				const updatedColorVariants = [...colorVariants];
				updatedColorVariants[variantIndex].colorImage = updatedColorVariants[
					variantIndex
				].colorImage.filter((_, i) => i !== imgIndex);

				setFormData({ ...formData, colorVariants: updatedColorVariants });
				toast.success(
					response.data.message ? response.data.message : 'Image deleted successfully'
				);
			} else if (response.isError) {
				if (response.data.errors) {
					toast.error(response.data.errors || 'Failed to delete image');
				}
			}
		} catch (error) {
			console.error('Failed to delete image:', error);
			toast.error('Failed to delete image');
		} finally {
			setDeletingImages((prev) => ({ ...prev, [imageKey]: false }));
		}
	};
	if (loading) {
		return (
			<Box display="flex" justifyContent="center" my={4}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Grid2 container spacing={2}>
			<Grid2 size={{ xs: 12 }}>
				<Typography
					sx={{
						fontSize: { xs: 14, sm: 18, md: 20 },
						mt: { xs: 0, sm: 2 },
						fontWeight: 'bold',
					}}
					variant="h6"
					gutterBottom
				>
					Color Variants
				</Typography>
			</Grid2>

			{colorVariants.map((variant, variantIndex) => (
				<Grid2 size={{ xs: 12 }} key={variant._id || `new-${variantIndex}`}>
					<Box
						sx={{
							p: { xs: 1, sm: 3 },
							border: '1px solid #eee',
							borderRadius: 1,
							backgroundColor: '#fafafa',
						}}
					>
						<Grid2 container spacing={2}>
							<Grid2 size={{ xs: 12 }} sx={{ textAlign: 'right' }}>
								<IconButton
									disabled={disabled}
									onClick={() => handleRemoveColorVariant(variantIndex)}
									color="error"
									size="small"
								>
									<DeleteIcon />
								</IconButton>
							</Grid2>

							<Grid2 size={{ xs: 12, sm: 6 }}>
								<TextField
									disabled={disabled}
									fullWidth
									label="Color Name"
									value={variant.colorName}
									onChange={(e) =>
										handleColorVariantChange(
											variantIndex,
											'colorName',
											e.target.value
										)
									}
									required
									size="small"
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, sm: 6 }}>
								<MuiColorInput
									disabled={disabled}
									fullWidth
									label="Color Code"
									value={variant.colorCode}
									onChange={(value) =>
										handleColorVariantChange(variantIndex, 'colorCode', value)
									}
									required
									format="hex"
									size="small"
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, sm: 6 }}>
								<TextField
									fullWidth
									disabled={disabled}
									label="Price"
									name="price"
									type="number"
									value={variant.price}
									onChange={(e) =>
										handleColorVariantChange(
											variantIndex,
											'price',
											e.target.value
										)
									}
									required
									size="small"
									inputProps={{ min: '1' }}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, sm: 6 }}>
								<TextField
									disabled={disabled}
									fullWidth
									label="Strikout Price"
									type="number"
									name="strickoutPrice"
									value={variant.strickoutPrice}
									onChange={(e) =>
										handleColorVariantChange(
											variantIndex,
											'strickoutPrice',
											e.target.value
										)
									}
									size="small"
									inputProps={{ min: '1' }}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, sm: 6 }}>
								<Typography variant="subtitle2" gutterBottom>
									Available Sizes *
								</Typography>
								<ToggleButtonGroup
									disabled={disabled}
									value={variant.sizes.map((sizeObj) => sizeObj.size)}
									onChange={(e, newSizes) => {
										const newSizesArray = newSizes.map((size: string) => ({
											size,
										}));
										handleColorVariantChange(
											variantIndex,
											'sizes',
											newSizesArray
										);
									}}
									aria-label="size selection"
									sx={{ flexWrap: 'wrap', gap: 1 }}
								>
									{sizes.map((size) => (
										<ToggleButton
											key={size}
											value={size}
											aria-label={size}
											disabled={disabled}
											sx={{
												minWidth: 60,
												height: 40,
												padding: '6px 12px !important',
												borderRadius: '6px !important',
												border: '1px solid #0C8040 !important',
												color: 'green',
												textTransform: 'none',
												fontWeight: 'bold',
												'&.Mui-selected': {
													backgroundColor: '#0C8040',
													color: 'white',
													borderColor: '#0C8040',
													padding: '6px 12px',
													borderRadius: '6px',
												},
												'&.Mui-selected:hover': {
													backgroundColor: '#006400',
												},
												'&:hover': {
													backgroundColor: 'rgba(0,128,0,0.1)',
												},
											}}
										>
											{size}
										</ToggleButton>
									))}
								</ToggleButtonGroup>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6 }}>
								<Typography variant="subtitle2" gutterBottom>
									Color Images *
								</Typography>

								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
									{variant.colorImage.map((imageUrl, imageIndex) => (
										<Box
											key={imageIndex}
											sx={{
												position: 'relative',
												width: 100,
												borderRadius: 1,
												overflow: 'hidden',
												border: '1px solid #ddd',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												padding: 1,
											}}
										>
											<Box
												component="img"
												src={
													imageUrl?.preview ||
													(imageUrl?.url?.startsWith('http')
														? imageUrl.url
														: `${process.env.REACT_APP_BASE_IMAGE_URL}${imageUrl.url}`)
												}
												alt={
													variant.imageAltTag?.[imageIndex] ||
													`Image ${imageIndex + 1}`
												}
												sx={{
													width: '100%',
													height: 80,
													objectFit: 'cover',
													borderRadius: 1,
												}}
											/>

											{deletingImages[`${variantIndex}-${imageIndex}`] ? (
												<CircularProgress size={20} />
											) : (
												<IconButton
													sx={{
														position: 'absolute',
														top: 4,
														right: 4,
														backgroundColor: 'rgba(0,0,0,0.5)',
														color: 'white',
														'&:hover': {
															backgroundColor: 'rgba(0,0,0,0.7)',
														},
													}}
													size="small"
													onClick={() =>
														handleRemoveImage(variantIndex, imageIndex)
													}
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											)}

											<TextField
												disabled={disabled}
												fullWidth
												size="small"
												label="Image Alt Text"
												value={variant.imageAltTag?.[imageIndex] || ''}
												onChange={(e) =>
													handleImageAltTagChange(
														variantIndex,
														imageIndex,
														e.target.value
													)
												}
												sx={{ mt: 1 }}
											/>
										</Box>
									))}
								</Box>

								<input
									type="file"
									id={`color-image-upload-${variantIndex}`}
									accept="image/*"
									multiple
									onChange={(e) => handleColorImageChange(variantIndex, e)}
									style={{ display: 'none' }}
									disabled={disabled}
								/>
								<label htmlFor={`color-image-upload-${variantIndex}`}>
									<Button
										variant="outlined"
										component="span"
										sx={{ mt: 1 }}
										startIcon={<AddCircleIcon />}
										disabled={disabled}
									>
										Add Images
									</Button>
								</label>
							</Grid2>
						</Grid2>
					</Box>
				</Grid2>
			))}

			<Grid2 size={{ xs: 12, sm: 12 }}>
				<Button
					variant="contained"
					onClick={handleAddColorVariant}
					startIcon={<AddCircleIcon />}
					sx={{ mt: 1 }}
					disabled={disabled}
				>
					Add Color Variant
				</Button>
			</Grid2>
		</Grid2>
	);
};

export default ColorVariants;
